import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Link, Server, Mail, HardDrive, Calendar, Check, X, ExternalLink, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../Common/Button';
import ConfirmModal from '../Common/ConfirmModal';
import AddConnectionModal from './AddConnectionModal';
import serviceConnectionsService from '../../services/serviceConnectionsService';

const ConnectionsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.bg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ConnectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  flex: 1;
  min-height: 0;
`;

const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CategoryIcon = styled.div`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const CategoryTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const ConnectionCount = styled.span`
  background: ${({ theme }) => theme.colors.primary[500]}20;
  color: ${({ theme }) => theme.colors.primary[400]};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
`;

const ConnectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  flex: 1;
  overflow-y: auto;
`;

const ConnectionCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
`;

const ConnectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ConnectionAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme, type }) => {
    switch (type) {
      case 'gmail': return '#EA4335';
      case 'drive': return '#4285F4';
      case 'calendar': return '#FBBC04';
      default: return theme.colors.gray[500];
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const ConnectionDetails = styled.div`
  flex: 1;
`;

const ConnectionName = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.75rem;
  color: ${({ theme, status }) => {
    switch (status) {
      case 'connected': return theme.colors.status.online;
      case 'disconnected': return theme.colors.status.error;
      default: return theme.colors.dark.textSecondary;
    }
  }};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
`;

const ConnectionActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ConnectionMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

// Service configuration
const serviceConfig = {
  gmail: {
    name: 'Gmail',
    icon: Mail,
    color: '#EA4335',
    description: 'Read & Send Emails'
  },
  drive: {
    name: 'Google Drive',
    icon: HardDrive,
    color: '#4285F4',
    description: 'Access Files'
  },
  calendar: {
    name: 'Google Calendar',
    icon: Calendar,
    color: '#FBBC04',
    description: 'Manage Events'
  }
};

const Connections = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingService, setConnectingService] = useState(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectingService, setDisconnectingService] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Fetch connections on mount
  useEffect(() => {
    loadConnections();
    
    // Cleanup polling on unmount
    return () => {
      serviceConnectionsService.stopPolling();
    };
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await serviceConnectionsService.fetchConnections();
      setConnections(data);
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConnectionInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase();
  };

  const handleConnect = async (service) => {
    try {
      setConnectingService(service);
      const result = await serviceConnectionsService.initiateConnection(service);
      
      if (result.already_connected) {
        alert(`Already connected as ${result.service_email}`);
        setConnectingService(null);
        return;
      }
      
      if (result.success) {
        // Connection successful, reload connections
        await loadConnections();
      }
    } catch (error) {
      console.error('Connection failed:', error);
      alert('Failed to connect. Please try again.');
    } finally {
      setConnectingService(null);
    }
  };

  const handleDisconnectClick = (service) => {
    setDisconnectingService(service);
    setShowDisconnectModal(true);
  };

  const handleConfirmDisconnect = async () => {
    if (!disconnectingService) return;
    
    setIsDisconnecting(true);
    try {
      await serviceConnectionsService.disconnect(disconnectingService);
      console.log('✅ Service disconnected successfully');
      await loadConnections();
      setShowDisconnectModal(false);
      setDisconnectingService(null);
      
      toast.success(`${serviceConfig[disconnectingService]?.name || disconnectingService} disconnected successfully`, {
        duration: 3000,
      });
    } catch (error) {
      console.error('❌ Disconnect failed:', error);
      toast.error('Failed to disconnect. Please try again.', {
        duration: 5000,
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const renderServiceCard = (serviceKey) => {
    const service = serviceConfig[serviceKey];
    const connection = connections.find(conn => conn.service === serviceKey);
    const isConnected = connection && connection.connected;
    const isConnecting = connectingService === serviceKey;
    const ServiceIcon = service.icon;

    return (
      <ConnectionCard
        key={serviceKey}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <ConnectionHeader>
          <ConnectionInfo>
            <ConnectionAvatar type={serviceKey} style={{ background: service.color + '20' }}>
              <ServiceIcon style={{ width: '20px', height: '20px', color: service.color }} />
            </ConnectionAvatar>
            <ConnectionDetails>
              <ConnectionName>{service.name}</ConnectionName>
              <ConnectionStatus status={isConnected ? 'connected' : 'disconnected'}>
                <StatusDot />
                {isConnected ? 'Connected' : 'Not Connected'}
              </ConnectionStatus>
            </ConnectionDetails>
          </ConnectionInfo>
          
          <ConnectionActions>
            {isConnected ? (
              <Button 
                variant="ghost" 
                size="xs"
                onClick={() => handleDisconnectClick(serviceKey)}
              >
                <X />
              </Button>
            ) : (
              <Button 
                variant="primary" 
                size="xs"
                onClick={() => handleConnect(serviceKey)}
                disabled={isConnecting}
              >
                {isConnecting ? <Loader style={{ animation: 'spin 1s linear infinite' }} /> : <Plus />}
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            )}
          </ConnectionActions>
        </ConnectionHeader>
        
        <ConnectionMeta>
          {isConnected && connection.service_email && (
            <MetaItem>
              <span>Account:</span>
              <span>{connection.service_email}</span>
            </MetaItem>
          )}
          <MetaItem>
            <span>Permissions:</span>
            <span>{service.description}</span>
          </MetaItem>
          {isConnected && connection.connected_at && (
            <MetaItem>
              <span>Connected:</span>
              <span>{new Date(connection.connected_at).toLocaleDateString()}</span>
            </MetaItem>
          )}
        </ConnectionMeta>
      </ConnectionCard>
    );
  };

  const hasConnections = connections.length > 0;

  return (
    <ConnectionsContainer>
      <Header>
        <HeaderLeft>
          <div>
            <Title>Connections</Title>
            <Subtitle>Connect Gmail, Drive, and Calendar to enhance SARVIK</Subtitle>
          </div>
        </HeaderLeft>
        
        {hasConnections && (
          <HeaderActions>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddModal(true)}
            >
              <Plus />
              Add Connection
            </Button>
          </HeaderActions>
        )}
      </Header>

      {loading ? (
        <EmptyState>
          <Loader style={{ animation: 'spin 1s linear infinite', width: '40px', height: '40px' }} />
          <p style={{ marginTop: '16px', fontSize: '0.875rem' }}>Loading connections...</p>
        </EmptyState>
      ) : (
        <ConnectionsGrid>
          <CategorySection>
            <CategoryHeader>
              <CategoryIcon><Server /></CategoryIcon>
              <CategoryTitle>App Connections</CategoryTitle>
              <ConnectionCount>{connections.filter(c => c.connected).length}/{Object.keys(serviceConfig).length}</ConnectionCount>
            </CategoryHeader>
            
            <ConnectionsList>
              {Object.keys(serviceConfig).map(renderServiceCard)}
            </ConnectionsList>
          </CategorySection>
        </ConnectionsGrid>
      )}

      <AnimatePresence>
        {showAddModal && (
          <AddConnectionModal
            onClose={() => setShowAddModal(false)}
            onConnect={handleConnect}
            serviceConfig={serviceConfig}
            connections={connections}
            connectingService={connectingService}
          />
        )}
      </AnimatePresence>

      {/* Disconnect Confirmation Modal */}
      <ConfirmModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setDisconnectingService(null);
        }}
        onConfirm={handleConfirmDisconnect}
        title="Disconnect Service"
        subtitle="This will revoke access to your account"
        message={`Are you sure you want to disconnect ${serviceConfig[disconnectingService]?.name || disconnectingService}? You will need to reconnect and authorize again to use this service.`}
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        icon={X}
        loading={isDisconnecting}
        loadingText="Disconnecting..."
      />
    </ConnectionsContainer>
  );
};

export default Connections;